<template>
  <div id="app" :class="{ 'not-logged-in': !user, 'help-mode': helpMode }">
    <template v-if="user || $route.name == 'login'">
      <TransitionGroup name="fade" mode="out-in">
        <div v-if="ready" key="app">
          <div id="nav">
            <div
              class="menu-button"
              data-help="Mit dem Menü Button kannst du das App Menü ein- und ausblenden um über verschiedene Menüpunkte in der App zu navigieren."
              data-help-title="Menü Button"
              @click="showMenu"
            >
              <BurgerButton />
            </div>
            <div
              v-if="
                !$route.meta.hideProfile ||
                ($route.meta.hideProfile !== undefined &&
                  $route.meta.hideProfile === false)
              "
              class="profile-widget-container"
            >
              <TheProfileWidget v-if="$route.meta.pageType !== 'test'" />
            </div>
            <div
              v-if="
                user &&
                myDataLoaded &&
                $route.meta.pageType !== 'test' &&
                ($route.meta.hideProfile == undefined ||
                  ($route.meta.hideProfile !== undefined &&
                    $route.meta.hideProfile === false))
              "
              :class="[profileExpanded ? 'expanded' : '', 'profile-expand-btn']"
              data-help-title="Profil ein-/ausblenden"
              data-help="Mit diesem Button kannst du dein Profil ein- bzw. ausklappen. Hier siehst du detaillierte Informationen zu deinem Lernfortschritt."
              @click="expandProfile"
            >
              <ExpandButton />
            </div>
          </div>
          <TheMenu
            :active="menuActive"
            @close-menu="toggleMenu"
            @logout="logout"
          />
          <TheBreadcrumbs v-if="breadcrumbs && breadcrumbs.length > 0" />
          <Transition name="fade" mode="out-in">
            <div v-if="ready" class="router-view-container">
              <RouterView :key="$route.name" />
            </div>
          </Transition>
          <Transition name="fade" mode="out-in">
            <TheDictEntry
              v-if="dictEntry"
              :entry="dictEntry"
              @close-dict="dictEntry = null"
            />
          </Transition>
          <Transition name="fade" mode="out-in" appear>
            <Popup v-if="curSolution" @close="resetCurSolution">
              <QuestionSolution :question="curSolution" />
            </Popup>
          </Transition>
          <Transition name="fade" mode="out-in" appear>
            <Popup v-if="configureCar" @close="setConfigureCar(false)">
              <TheCarConfigurator :mode="configureCar" />
            </Popup>
          </Transition>
          <TheFooter />
        </div>
        <div v-else key="loading">
          <div class="loading-screen">
            <CubeSpinner />
            <p>Einen Moment bitte...</p>
          </div>
        </div>
      </TransitionGroup>
    </template>
    <template v-else>
      <RouterView
        v-if="$route.name === 'imprint' || $route.name === 'changelog'"
      />
      <div v-else class="start-view-container">
        <div class="logo">
          <img src="@/assets/logo-claim-vert-neg.png" />
        </div>
        <div class="login-link">
          <router-link to="/login">Anmelden</router-link>
        </div>
        <div class="logo-list-container">
          <a href="https://www.bmnt.gv.at/"
            ><img
              src="@/assets/logos/bund.png"
              alt="mit Unterstützung von Bund, Land und Europäischer Union"
          /></a>
          <a
            href="https://ec.europa.eu/agriculture/rural-development-2014-2020_de"
            ><img
              src="@/assets/logos/le14-20.png"
              alt="mit Unterstützung von Bund, Land und Europäischer Union"
          /></a>
          <img src="@/assets/logos/vlbg.png" alt="Logo Vorarlberg" />
          <img src="@/assets/logos/eu.png" alt="Logo EU" />
          <a
            href="https://www.capito.eu/team/capito-vorarlberg-und-liechtenstein/"
            ><img src="@/assets/logos/capito.png" alt="capito logo"
          /></a>
          <a href="https://www.dafuer.at/de"
            ><img src="@/assets/logos/dafur.png" alt="dafür logo"
          /></a>
        </div>
      </div>
    </template>
    <Transition name="fade" mode="out-in" appear>
      <Popup v-if="updateExists" @close="refreshApp">
        <h1>Update verfübar!</h1>
        <p>Ein Update für die F-EASY App ist verfügbar.</p>
        <BaseButton wide @click="refreshApp">Jetzt Installieren</BaseButton>
      </Popup>
    </Transition>
    <Transition name="fade" mode="out-in" appear>
      <Popup v-if="popupMessage" @close="setPopupMessage(null)">
        <h1 v-if="popupMessageTitle">{{ popupMessageTitle }}</h1>
        <p v-html="popupMessage"></p>
      </Popup>
    </Transition>
  </div>
</template>

<script>
import TheMenu from "./components/TheMenu.vue";
import TheProfileWidget from "./components/TheProfileWidget.vue";
import TheBreadcrumbs from "./components/TheBreadcrumbs.vue";
import TheFooter from "./components/TheFooter.vue";
import TheDictEntry from "./components/TheDictEntry.vue";
import BaseButton from "./components/BaseButton.vue";
import QuestionSolution from "@/components/QuestionSolution.vue";
import BurgerButton from "@/assets/BurgerButton.svg";
import ExpandButton from "@/assets/ExpandButton.svg";
import Popup from "@/components/Popup.vue";
import TheCarConfigurator from "@/components/TheCarConfigurator.vue";
import CubeSpinner from "@/components/CubeSpinner.vue";
import axios from "@/http";
import firebase from "firebase/app";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "App",
  components: {
    TheMenu,
    BaseButton,
    BurgerButton,
    TheProfileWidget,
    TheBreadcrumbs,
    TheFooter,
    TheDictEntry,
    ExpandButton,
    QuestionSolution,
    Popup,
    TheCarConfigurator,
    CubeSpinner,
  },
  computed: {
    ...mapGetters([
      "tokenHeader",
      "questionClasses",
      "dictionary",
      "user",
      "profileExpanded",
      "curSolution",
      "currentQuestionClass",
      "configureCar",
      "breadcrumbs",
      "popupMessage",
      "popupMessageTitle",
      "helpMode",
    ]),
  },
  watch: {
    "$route.meta.classSlug": function () {
      this.loadSubjects();
    },
  },
  data() {
    return {
      menuActive: false,
      ready: false,
      dictEntry: null,
      myDataLoaded: false,
      refreshing: false,
      updateExists: false,
      registration: null,
    };
  },
  created() {
    let vm = this;
    if ("serviceWorker" in navigator) {
      document.addEventListener(
        "swUpdated",
        function (e) {
          vm.updateExists = true;
          vm.registration = e.detail;
        },
        { once: true }
      );
      navigator.serviceWorker.addEventListener("controllerchange", (e) => {
        if (vm.refreshing) return;
        vm.refreshing = true;
        window.location.reload();
      });
    }
  },
  async mounted() {
    let vm = this;
    document.addEventListener("click", function (e) {
      if (event.target.getAttribute("data-help")) {
        let title = event.target.getAttribute("data-help-title");
        title = title || "Hilfe";
        vm.setPopupMessageTitle(title);
        vm.setPopupMessage(event.target.getAttribute("data-help"));
      }

      if (!event.target.matches(".dict-link") || !vm.dictionary) return;
      let result = vm.dictionary.filter(
        (d) =>
          d.dict_text.toLowerCase() ==
          e.target.getAttribute("data-word").toLowerCase()
      );
      if (result.length > 0) {
        vm.dictEntry = result[0];
      }
    });

    this.ready = false;
    try {
      let result = await axios.get(
        process.env.VUE_APP_FLL_ROOT_API + "/classes",
        {
          headers: {
            ...this.tokenHeader,
          },
        }
      );
      this.setQuestionClasses(result.data);
    } catch (error) {
      // TODO: handle error
      console.error(error); // eslint-disable-line
    }
    this.loadTerms();
    if (this.$route.meta.classSlug) {
      await this.loadSubjects();
    }
    await this.loadMyData();
    this.ready = true;
  },
  methods: {
    toggleMenu() {
      this.menuActive = !this.menuActive;
      this.retractProfile();
    },
    async loadMyData() {
      if (this.tokenHeader) {
        try {
          let result = await axios.get(
            process.env.VUE_APP_FLL_ROOT_API + "/me",
            {
              headers: {
                ...this.tokenHeader,
              },
            }
          );

          this.myDataLoaded = true;
          if (!result.data.meta.car) {
            result.data.meta.car = {
              meta_value: {
                car: null,
                color: null,
                rims: null,
                windows: null,
                charm: null,
              },
            };
          }
          this.setMe(result.data);
          this.setAnswers(result.data.totalAnswers);
          this.setTodaysAnswers(result.data.todaysAnswers);
          this.setTotalProgress(result.data.totalProgress);
          this.setTotalCountedProgress(result.data.totalCountedProgress);
          //this.terms = result.data;
        } catch (error) {
          // TODO: handle error
          this.setPopupMessageTitle("Hinweis");
          this.setPopupMessage(
            "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an"
          );
          this.$router.push("/login");
          this.setMe({});
          console.error(error); // eslint-disable-line
        }
      }
    },
    async loadTerms() {
      try {
        let result = await axios.get(
          process.env.VUE_APP_FLL_ROOT_API + "/begriffe",
          {
            headers: {
              ...this.tokenHeader,
            },
          }
        );

        this.setDictionary(result.data);
        //this.terms = result.data;
      } catch (error) {
        // TODO: handle error
        console.error(error); // eslint-disable-line
      }
    },
    expandProfile() {
      if (this.helpMode) return;
      this.$store.dispatch("toggleProfileExpanded");
    },
    async loadSubjects() {
      this.setSubjects([]);
      let result = await axios.get(
        process.env.VUE_APP_FLL_ROOT_API +
          "/classes/" +
          this.$route.meta.classSlug +
          "/subjects",
        {
          headers: {
            ...this.tokenHeader,
          },
        }
      );
      this.setSubjects(result.data);
    },
    logout() {
      firebase.auth().signOut();
      this.setPopupMessageTitle("Hinweis");
      this.setPopupMessage("Du wurdest abgemeldet.");
    },
    refreshApp() {
      this.updateExists = false;
      if (!this.registration || !this.registration.waiting) {
        return;
      }
      this.registration.waiting.postMessage("skipWaiting");
    },
    showMenu() {
      if (this.helpMode) return;
      this.toggleMenu();
    },
    ...mapActions([
      "setQuestionClasses",
      "setDictionary",
      "setAnswers",
      "setTodaysAnswers",
      "setTotalProgress",
      "setTotalProgress",
      "setTotalCountedProgress",
      "setMe",
      "resetCurSolution",
      "retractProfile",
      "setSubjects",
      "setConfigureCar",
      "setCar",
      "setColor",
      "setRims",
      "setWindows",
      "setCharm",
      "setPopupMessage",
      "setPopupMessageTitle",
    ]),
  },
};
</script>

<style lang="scss">
/* roboto-300 - latin */
@font-face {
  font-family: "Roboto";
  font-style: normal;
  font-weight: 300;
  src: url("/fonts/roboto-v30-latin-300.eot"); /* IE9 Compat Modes */
  src: local(""),
    url("/fonts/roboto-v30-latin-300.eot?#iefix") format("embedded-opentype"),
    /* IE6-IE8 */ url("/fonts/roboto-v30-latin-300.woff2") format("woff2"),
    /* Super Modern Browsers */ url("/fonts/roboto-v30-latin-300.woff")
      format("woff"),
    /* Modern Browsers */ url("/fonts/roboto-v30-latin-300.ttf")
      format("truetype"),
    /* Safari, Android, iOS */ url("/fonts/roboto-v30-latin-300.svg#Roboto")
      format("svg"); /* Legacy iOS */
}
/* roboto-regular - latin */
@font-face {
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  src: url("/fonts/roboto-v30-latin-regular.eot"); /* IE9 Compat Modes */
  src: local(""),
    url("/fonts/roboto-v30-latin-regular.eot?#iefix")
      format("embedded-opentype"),
    /* IE6-IE8 */ url("/fonts/roboto-v30-latin-regular.woff2") format("woff2"),
    /* Super Modern Browsers */ url("/fonts/roboto-v30-latin-regular.woff")
      format("woff"),
    /* Modern Browsers */ url("/fonts/roboto-v30-latin-regular.ttf")
      format("truetype"),
    /* Safari, Android, iOS */ url("/fonts/roboto-v30-latin-regular.svg#Roboto")
      format("svg"); /* Legacy iOS */
}

/* zcool-qingke-huangyou-regular - latin */
@font-face {
  font-family: "ZCOOL QingKe HuangYou";
  font-style: normal;
  font-weight: 400;
  src: url("/fonts/zcool-qingke-huangyou-v13-latin-regular.eot"); /* IE9 Compat Modes */
  src: local(""),
    url("/fonts/zcool-qingke-huangyou-v13-latin-regular.eot?#iefix")
      format("embedded-opentype"),
    /* IE6-IE8 */ url("/fonts/zcool-qingke-huangyou-v13-latin-regular.woff2")
      format("woff2"),
    /* Super Modern Browsers */
      url("/fonts/zcool-qingke-huangyou-v13-latin-regular.woff") format("woff"),
    /* Modern Browsers */
      url("/fonts/zcool-qingke-huangyou-v13-latin-regular.ttf")
      format("truetype"),
    /* Safari, Android, iOS */
      url("/fonts/zcool-qingke-huangyou-v13-latin-regular.svg#ZCOOLQingKeHuangYou")
      format("svg"); /* Legacy iOS */
}

#app {
  /*max-width: 500px;*/
  font-size: 16px;
  font-family: "Roboto", sans-serif;
  min-height: 100vh;
  margin: 0 auto;
  max-width: $app-max-width;
  min-width: $app-min-width;
  position: relative;
  overflow: hidden;
  padding-bottom: $footer-height;

  .component {
    padding: 20px;
  }

  &.not-logged-in {
    padding-bottom: 0;
  }
}

#nav {
  position: relative;
  .menu-button {
    cursor: pointer;
    position: absolute;
    z-index: 1;
    width: 32px;
    padding: 10px 4px 4px 18px;
    box-sizing: content-box;

    svg {
      width: 100%;
      height: auto;
    }
  }

  .profile-expand-btn {
    position: absolute;
    bottom: -15px;
    right: 26%;
    cursor: pointer;
    transition: transform 0.4s ease-in-out;
    transform: rotate(0);
    user-select: none;

    &.expanded {
      transform: rotate(180deg);
    }
  }
}

.logo-list-container {
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  /*flex-direction: column;*/
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 10px 10px 10px;
  background-color: #fff;
  box-sizing: border-box;

  img {
    display: block;
    max-width: 200px;
    height: auto;
  }
}

.router-view-container {
  margin: 0 auto;
}

.page-title-container {
  background: $blue;
  color: #ffffff;
  font-size: 12px;
  padding: 8px;
}

h1.page-title-container {
  margin: 0;
}

.view-container {
  padding: $default-container-padding;
}

.loading-screen {
  background: $blue;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;

  p {
    color: #fff;
  }
}

.start-view-container {
  background: $blue;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  padding-top: 20px;

  @media all and (max-width: 420px) {
    justify-content: flex-start;
    padding-top: 20px;
    .login-link {
      background: $blue;
    }
  }

  @media all and (max-height: 700px) {
    justify-content: flex-start;
    padding-top: 20px;
    .login-link {
      background: $blue;
    }
  }

  @media all and (max-width: 420px) and (max-height: 710px) {
    justify-content: flex-start;
    padding-top: 20px;
    .logo {
      max-width: 50%;
    }
    .login-link {
      background: $blue;
    }
  }

  .login-link {
    position: relative;
    z-index: 100;
    a {
      font-size: 30px;
      color: #fff;
      padding: 5px 20px;
      display: block;
    }
  }

  .logo {
    margin-bottom: 20px;
    padding: 5px;
    text-align: center;
    width: 100%;
    position: relative;
    z-index: 100;
    background: $blue;
    img {
      max-width: 75%;
      height: auto;
    }
  }
}

*[data-help] {
  &:after {
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
  }
}

.help-mode {
  *[data-help] {
    position: relative;
    &:after {
      opacity: 1;
      position: absolute;
      display: flex;
      content: "";
      z-index: 2;
      top: calc(50% - 16px);
      left: calc(50% - 16px);
      width: 32px;
      height: 32px;
      justify-content: center;
      align-items: center;
      background: url("assets/Icon_Frage.png");
      font-weight: bold;
      font-size: 20px;
      cursor: pointer;
      pointer-events: all;
      border-radius: 5px;
      box-sizing: content-box;
      box-shadow: 0px 0px 15px #000000ad;
    }
  }

  .menu-button {
    &:after {
      left: calc(50% + 30px);
    }
  }

  .profile-expand-btn {
    &:after {
      left: calc(50% + 20px);
    }
    &.expanded {
      &:after {
        transform: rotate(180deg);
        left: calc(50% + 20px);
      }
    }
  }
}

.install-button {
  display: block;
}
</style>
